// Shared fetch client for product API calls. Replaces the per-app `request<T>` /
// `apiFetch<T>` / `api<T>` variants that drifted across the SPAs.
//
//   // Pass a THUNK so the base URL is resolved per-request, not at module load:
//   const api = createApiClient(() => getEnv("BOARD_API_URL"));
//   const teams = await api.request<Team[]>("/api/teams");
//
// Why a thunk: in dev, `bootstrapDevEnv()` populates `window.__ENV__` at runtime — but a
// module-level `createApiClient(getEnv("X"))` would read it before that runs (module bodies
// evaluate before main.tsx's body), capturing an empty base → relative URLs → the SPA's own
// origin instead of the API. Resolving lazily reads `getEnv` at request time, after bootstrap.
//
// Defaults to cookie auth (`credentials: "include"`); pass `{ credentials: "omit" }`
// for public APIs (e.g. help-web's product search).

export class ApiError extends Error {
  constructor(readonly status: number, readonly body: string, statusText = "") {
    super(`API error ${status}: ${body || statusText}`);
    this.name = "ApiError";
  }
}

export interface ApiClient {
  request<T>(path: string, options?: RequestInit): Promise<T>;
}

export interface ApiClientOptions {
  /** fetch credentials mode. Defaults to "include" (send the auth cookie cross-origin). */
  credentials?: RequestCredentials;
}

export function createApiClient(
  baseUrl: string | (() => string),
  opts: ApiClientOptions = {}
): ApiClient {
  const credentials = opts.credentials ?? "include";

  return {
    async request<T>(path: string, options: RequestInit = {}): Promise<T> {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        ...((options.headers as Record<string, string>) ?? {}),
      };

      const base = typeof baseUrl === "function" ? baseUrl() : baseUrl;
      const res = await fetch(`${base}${path}`, { ...options, headers, credentials });

      if (!res.ok) {
        const body = await res.text().catch(() => "");
        throw new ApiError(res.status, body, res.statusText);
      }

      // No content (e.g. 204 from DELETE/PUT) — nothing to parse.
      if (res.status === 204 || res.headers.get("content-length") === "0") {
        return undefined as T;
      }

      return res.json();
    },
  };
}
